import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CreditCard, Download, ReceiptText, Check, ArrowRight, ShieldCheck, Clock, CalendarClock, FlaskConical } from "lucide-react";
import api, { fmtDay, inr, formatErr } from "../../lib/api";
import { Loading, PageHeader, StatusPill, EmptyState } from "../../components/Shared";

function PlanCard({ p, selected, cta, onSelect, testidPrefix }) {
  return (
    <div className={`relative rounded-3xl border p-6 transition-shadow hover:shadow-lg ${p.highlighted ? "border-emerald-800 bg-emerald-950 text-white shadow-xl" : "border-stone-200 bg-white"}`}
      data-testid={`${testidPrefix}-plan-card-${p.name.toLowerCase()}`}>
      {p.highlighted && <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-terracotta px-4 py-1 text-xs font-bold uppercase tracking-wider text-white">Most chosen</span>}
      <h3 className="font-heading text-xl font-bold">{p.name}</h3>
      <p className="mt-2"><span className="font-heading text-3xl font-extrabold">{inr(p.price)}</span>
        <span className={`text-sm ${p.highlighted ? "text-stone-300" : "text-stone-500"}`}> /{p.period}</span></p>
      <p className={`mt-1 text-xs ${p.highlighted ? "text-stone-300" : "text-stone-500"}`}>{p.visits_per_month} home & welfare visits per month</p>
      <ul className="mt-5 space-y-2">
        {(p.benefits || []).map((b) => (
          <li key={b} className="flex items-start gap-2 text-sm">
            <Check className={`mt-0.5 h-4 w-4 shrink-0 ${p.highlighted ? "text-emerald-300" : "text-emerald-700"}`} /> {b}
          </li>
        ))}
      </ul>
      <button onClick={() => onSelect(p)} data-testid={`${testidPrefix}-select-${p.name.toLowerCase()}`}
        className={`mt-6 flex w-full items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold transition-colors ${selected ? "bg-stone-300 text-stone-600 cursor-default" : p.highlighted ? "bg-white text-emerald-950 hover:bg-stone-100" : "bg-emerald-900 text-white hover:bg-emerald-800"}`}>
        {selected ? "Currently selected" : cta} {!selected && <ArrowRight className="h-4 w-4" />}
      </button>
    </div>
  );
}

export default function Billing() {
  const [d, setD] = useState(null);
  const [checkoutFor, setCheckoutFor] = useState(null); // plan object pending payment
  const [quote, setQuote] = useState(null); // change-plan quote
  const [changingTo, setChangingTo] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/family/billing").then((r) => setD(r.data)).catch((e) => { toast.error(formatErr(e)); setD({}); });
  useEffect(() => { load(); }, []);
  if (!d) return <Loading />;

  const { subscription: sub, plan, plans = [], invoices = [], payments = [], assessment_status } = d;
  const assessmentDone = ["assessment_completed", "active"].includes(assessment_status);
  const isActive = sub?.status === "active";
  const isPendingAssessment = sub?.status === "pending_assessment";
  const isPendingPayment = sub?.status === "pending_payment";

  const downloadReceipt = (inv) => {
    const text = `PARENTONE CARE PRIVATE LIMITED\nReceipt ${inv.number}\n${inv.description}\nAmount: ${inr(inv.amount)} + GST ${inr(inv.gst)}\nTotal: ${inr(inv.total)}\nStatus: ${inv.status}\nIssued: ${fmtDay(inv.issued_at)}${inv.paid_at ? `\nPaid: ${fmtDay(inv.paid_at)}` : ""}`;
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([text], { type: "text/plain" }));
    a.download = `${inv.number}.txt`; a.click();
  };

  const selectPlan = async (p) => {
    if (sub && sub.plan_id === p.id) return;
    setBusy(true);
    try {
      const r = await api.post("/family/billing/select-plan", { plan_id: p.id });
      toast.success(assessmentDone ? `${p.name} selected — continue to payment` : `${p.name} reserved — completes after your assessment`);
      if (r.data.subscription?.status === "pending_payment") setCheckoutFor(p);
      await load();
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };

  const payNow = async () => {
    setBusy(true);
    try {
      const r = await api.post("/family/billing/checkout", {});
      toast.success(`Payment confirmed — ${r.data.plan.name} plan is now active`);
      setCheckoutFor(null);
      await load();
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };

  const requestQuote = async (p) => {
    setBusy(true);
    try {
      const r = await api.post("/family/billing/change-plan", { plan_id: p.id });
      setQuote(r.data.quote); setChangingTo(p);
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };

  const confirmChange = async () => {
    setBusy(true);
    try {
      const r = await api.post("/family/billing/change-plan", { plan_id: changingTo.id, confirm: true });
      toast.success(r.data.quote.type === "upgrade" ? `Upgraded to ${changingTo.name}` : `Change to ${changingTo.name} scheduled for renewal`);
      setQuote(null); setChangingTo(null);
      await load();
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="billing-page">
      <PageHeader title="Plan & Billing" sub="Choose your care plan, manage your subscription, invoices and payment history." />

      {d.payment_mode === "test" && (
        <div className="mb-6 flex items-start gap-3 rounded-2xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900" data-testid="test-mode-banner">
          <FlaskConical className="mt-0.5 h-4 w-4 shrink-0" />
          <p><strong>Payments are in TEST mode.</strong> No real money is charged. Online payment gateway (UPI / cards / netbanking) is being connected and will go live shortly.</p>
        </div>
      )}

      {/* ---------- PENDING ASSESSMENT ---------- */}
      {isPendingAssessment && plan && (
        <div className="mb-6 rounded-3xl border border-emerald-200 bg-white p-6" data-testid="pending-assessment-banner">
          <div className="flex items-start gap-3">
            <Clock className="mt-1 h-5 w-5 shrink-0 text-emerald-800" />
            <div>
              <p className="font-heading text-lg font-bold text-stone-900">{plan.name} plan reserved — assessment pending</p>
              <p className="mt-1 text-sm text-stone-600">Your selected plan is held for you. Once our care team completes and approves your Parent Care Assessment, payment unlocks and your plan activates right after.</p>
              <p className="mt-2 text-sm font-semibold text-emerald-900">Need to book or follow up on your assessment? Call your care advisor or reply to your onboarding email.</p>
            </div>
          </div>
        </div>
      )}

      {/* ---------- PENDING PAYMENT ---------- */}
      {isPendingPayment && plan && (
        <div className="mb-6 rounded-3xl border border-emerald-300 bg-emerald-50 p-6" data-testid="pending-payment-banner">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="font-heading text-lg font-bold text-stone-900">Assessment complete — your {plan.name} plan is ready</p>
              <p className="mt-1 text-sm text-stone-700">{inr(plan.price)}/{plan.period} + 5% GST = <strong>{inr(Math.round(plan.price * 1.05))}</strong> payable now.</p>
            </div>
            <button onClick={() => setCheckoutFor(plan)} data-testid="continue-to-payment-btn"
              className="rounded-full bg-emerald-900 px-6 py-3 text-sm font-bold text-white hover:bg-emerald-800">
              Continue to payment <ArrowRight className="ml-1 inline h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* ---------- ACTIVE SUBSCRIPTION ---------- */}
      {isActive && plan && (
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="rounded-3xl bg-emerald-950 p-6 text-white" data-testid="subscription-overview">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-emerald-300"><CreditCard className="h-4 w-4" /> Current plan</div>
            <p className="mt-3 font-heading text-2xl font-bold">{plan.name}</p>
            <p className="mt-1 text-3xl font-extrabold">{inr(plan.price)}<span className="text-sm font-medium text-stone-300">/{plan.period}</span></p>
            <div className="mt-3"><StatusPill status={sub.status} /></div>
            <dl className="mt-4 space-y-1.5 text-sm text-stone-300">
              <div className="flex justify-between"><dt>Started</dt><dd className="font-semibold text-white">{fmtDay(sub.started_at)}</dd></div>
              <div className="flex justify-between"><dt>Renews</dt><dd className="font-semibold text-white">{fmtDay(sub.renews_at)}</dd></div>
              <div className="flex justify-between"><dt>Visits included</dt><dd className="font-semibold text-white">{plan.visits_per_month}/month</dd></div>
              <div className="flex justify-between"><dt>Billing</dt><dd className="font-semibold text-white">Monthly · GST invoice</dd></div>
            </dl>
            {sub.pending_plan_id && (
              <p className="mt-4 flex items-start gap-2 rounded-xl bg-emerald-900/60 p-3 text-xs text-emerald-200" data-testid="scheduled-change-note">
                <CalendarClock className="mt-0.5 h-4 w-4 shrink-0" /> Plan change scheduled for {fmtDay(sub.pending_change_effective_at)}. You keep {plan.name} benefits until then.
              </p>
            )}
            <p className="mt-5 text-xs text-stone-400">Add-on services (nurse, physio, ambulance…) are charged by verified partners and appear as separate line items.</p>
          </div>
          <div className="lg:col-span-2">
            <div className="p1-card p-6">
              <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-stone-900"><ShieldCheck className="h-5 w-5 text-emerald-800" /> Your plan includes</h2>
              <ul className="mt-4 grid gap-2 sm:grid-cols-2">
                {(plan.benefits || []).map((b) => (
                  <li key={b} className="flex items-start gap-2 text-sm text-stone-700"><Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-700" /> {b}</li>
                ))}
              </ul>
            </div>
            <h2 className="mb-3 mt-8 font-heading text-lg font-semibold text-stone-900">Change plan</h2>
            <div className="grid gap-4 sm:grid-cols-3">
              {plans.map((p) => (
                <div key={p.id} className={`rounded-2xl border p-4 ${p.id === plan.id ? "border-emerald-800 bg-emerald-50" : "border-stone-200 bg-white"}`} data-testid={`change-option-${p.name.toLowerCase()}`}>
                  <p className="font-heading font-bold text-stone-900">{p.name}</p>
                  <p className="text-sm text-stone-500">{inr(p.price)}/{p.period} · {p.visits_per_month} visits</p>
                  {p.id === plan.id
                    ? <span className="mt-3 inline-block rounded-full bg-emerald-900 px-3 py-1 text-xs font-bold text-white">Current plan</span>
                    : <button onClick={() => requestQuote(p)} disabled={busy} data-testid={`change-to-${p.name.toLowerCase()}`}
                        className="mt-3 w-full rounded-full border border-emerald-800 px-3 py-1.5 text-xs font-bold text-emerald-900 hover:bg-emerald-50">
                        {p.price > plan.price ? "Upgrade" : "Downgrade"}
                      </button>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ---------- NO SUBSCRIPTION: PLAN PICKER ---------- */}
      {!sub && (
        <div data-testid="plan-picker">
          <p className="mb-6 max-w-2xl text-sm text-stone-600">
            {assessmentDone
              ? "Your assessment is complete — choose a plan below and continue to payment to activate your care immediately."
              : "Choose a plan now — we will reserve it for you. Payment unlocks as soon as your Parent Care Assessment is completed and approved."}
          </p>
          {plans.length === 0 ? (
            <EmptyState title="Plans are being configured" body="Please talk to your care advisor for current pricing." />
          ) : (
            <div className="grid gap-6 lg:grid-cols-3">
              {plans.map((p) => <PlanCard key={p.id} p={p} cta={assessmentDone ? "Choose plan & pay" : "Choose plan"} onSelect={selectPlan} testidPrefix="family" />)}
            </div>
          )}
          <p className="mt-8 max-w-2xl text-sm text-stone-500">Prefer to pay by bank transfer or UPI directly? Our care team can record it for you — just call us.</p>
        </div>
      )}

      {/* ---------- PENDING: ALLOW SWITCHING SELECTED PLAN ---------- */}
      {(isPendingAssessment || isPendingPayment) && (
        <div className="mt-4" data-testid="selected-plan-switcher">
          <h2 className="mb-4 font-heading text-lg font-semibold text-stone-900">Your selected plan</h2>
          <div className="grid gap-6 lg:grid-cols-3">
            {plans.map((p) => <PlanCard key={p.id} p={p} selected={sub?.plan_id === p.id} cta={isPendingPayment ? "Switch & pay" : "Switch to this plan"} onSelect={selectPlan} testidPrefix="family" />)}
          </div>
        </div>
      )}

      {/* ---------- INVOICES & PAYMENTS ---------- */}
      <div className="mt-10">
        <h2 className="mb-3 flex items-center gap-2 font-heading text-lg font-semibold text-stone-900"><ReceiptText className="h-5 w-5 text-emerald-800" /> Invoices & receipts</h2>
        {invoices.length === 0 ? <EmptyState title="No invoices yet" body="Invoices and receipts appear here once your subscription begins." /> : (
          <div className="space-y-3">
            {invoices.map((inv) => (
              <div key={inv.id} className="p1-card flex flex-wrap items-center justify-between gap-3 p-4" data-testid={`invoice-row-${inv.id}`}>
                <div>
                  <p className="text-sm font-semibold text-stone-800">{inv.number} · {inv.description}</p>
                  <p className="text-xs text-stone-400">Issued {fmtDay(inv.issued_at)}{inv.demo ? " · demo" : ""}{inv.paid_at ? ` · Paid ${fmtDay(inv.paid_at)}` : ""}</p>
                </div>
                <div className="flex items-center gap-3">
                  <p className="font-heading font-bold text-stone-900">{inr(inv.total)}</p>
                  <StatusPill status={inv.status} />
                  <button onClick={() => downloadReceipt(inv)} className="rounded-full border border-stone-200 p-2 text-stone-500 hover:border-emerald-700 hover:text-emerald-900" aria-label="Download receipt" data-testid={`receipt-${inv.id}`}><Download className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
        <h2 className="mb-3 mt-8 font-heading text-lg font-semibold text-stone-900">Payment history</h2>
        {payments.length === 0 ? <p className="text-sm text-stone-500">No payments recorded yet.</p> : (
          <div className="space-y-2">
            {payments.map((p) => (
              <div key={p.id} className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-stone-200 bg-white p-4 text-sm" data-testid={`payment-row-${p.id}`}>
                <span className="text-stone-700">{p.method.replace(/_/g, " ")}{p.reference ? ` · Ref ${p.reference}` : ""} · {fmtDay(p.at)}</span>
                <span className="flex items-center gap-3"><strong>{inr(p.amount)}</strong><StatusPill status={p.status} /></span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ---------- CHECKOUT MODAL (TEST MODE) ---------- */}
      {checkoutFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/50 p-4" data-testid="checkout-modal">
          <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl">
            <h2 className="font-heading text-xl font-bold text-stone-900">Confirm your {checkoutFor.name} plan</h2>
            <div className="mt-4 space-y-2 rounded-2xl bg-stone-50 p-4 text-sm">
              <div className="flex justify-between"><span className="text-stone-500">Plan</span><strong>{checkoutFor.name} — {inr(checkoutFor.price)}/{checkoutFor.period}</strong></div>
              <div className="flex justify-between"><span className="text-stone-500">GST (5%)</span><strong>{inr(Math.round(checkoutFor.price * 0.05))}</strong></div>
              <div className="flex justify-between border-t border-stone-200 pt-2"><span className="text-stone-500">Payable now</span><strong className="text-emerald-900">{inr(Math.round(checkoutFor.price * 1.05))}</strong></div>
            </div>
            <p className="mt-3 flex items-start gap-2 rounded-xl bg-amber-50 p-3 text-xs text-amber-900" data-testid="checkout-test-note">
              <FlaskConical className="mt-0.5 h-4 w-4 shrink-0" /> TEST mode: confirming activates your subscription immediately and generates a GST invoice/receipt. No real payment is charged.
            </p>
            <div className="mt-5 flex gap-3">
              <button onClick={payNow} disabled={busy} data-testid="confirm-payment-btn"
                className="flex-1 rounded-full bg-emerald-900 px-6 py-3 text-sm font-bold text-white hover:bg-emerald-800 disabled:opacity-50">
                {busy ? "Processing…" : "Confirm & activate plan"}
              </button>
              <button onClick={() => setCheckoutFor(null)} data-testid="checkout-cancel" className="rounded-full border border-stone-200 px-5 py-3 text-sm font-semibold text-stone-600">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ---------- CHANGE PLAN CONFIRMATION ---------- */}
      {quote && changingTo && plan && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/50 p-4" data-testid="change-plan-modal">
          <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl">
            <h2 className="font-heading text-xl font-bold text-stone-900">Confirm plan change</h2>
            <div className="mt-4 space-y-2 rounded-2xl bg-stone-50 p-4 text-sm">
              <div className="flex justify-between"><span className="text-stone-500">Current plan</span><strong>{quote.current_plan.name} — {inr(quote.current_plan.price)}/mo</strong></div>
              <div className="flex justify-between"><span className="text-stone-500">New plan</span><strong>{quote.new_plan.name} — {inr(quote.new_plan.price)}/mo</strong></div>
              <div className="flex justify-between"><span className="text-stone-500">Amount payable now (incl. GST)</span><strong className="text-emerald-900">{inr(quote.total_payable_now)}</strong></div>
              <div className="flex justify-between"><span className="text-stone-500">Next renewal amount (incl. GST)</span><strong>{inr(quote.next_renewal_total)}</strong></div>
              <div className="flex justify-between"><span className="text-stone-500">Effective date</span><strong>{quote.type === "upgrade" ? "Immediately" : fmtDay(quote.effective_date)}</strong></div>
            </div>
            <p className="mt-3 text-xs text-stone-500">
              {quote.type === "upgrade"
                ? `You pay only the prorated difference for the ${quote.days_remaining} days remaining in this cycle. Your renewal date stays ${fmtDay(quote.renews_at)}.`
                : `You keep your current ${quote.current_plan.name} benefits until ${fmtDay(quote.effective_date)}. No refund for unused time on the current plan.`}
            </p>
            <div className="mt-5 flex gap-3">
              <button onClick={confirmChange} disabled={busy} data-testid="confirm-change-btn"
                className="flex-1 rounded-full bg-emerald-900 px-6 py-3 text-sm font-bold text-white hover:bg-emerald-800 disabled:opacity-50">
                {busy ? "Processing…" : quote.type === "upgrade" ? "Pay & upgrade now" : "Schedule change"}
              </button>
              <button onClick={() => { setQuote(null); setChangingTo(null); }} data-testid="change-cancel" className="rounded-full border border-stone-200 px-5 py-3 text-sm font-semibold text-stone-600">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
